import React, { useEffect, useRef, useState } from 'react';
import { Shield, ArrowRight, Star, Users, Brain, Sparkles } from 'lucide-react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { AreaChart, Area, ResponsiveContainer } from 'recharts';

const BRAIN_TREE_IMG = 'https://static.prod-images.emergentagent.com/jobs/6caceefd-cb80-4c8a-a8fe-90917c71e43e/images/13ac8fa80067e2207389c305abeffbbf4488c680904dccbf80242066b5a79838.png';
const NEURAL_BG = 'https://static.prod-images.emergentagent.com/jobs/6caceefd-cb80-4c8a-a8fe-90917c71e43e/images/af1be90ab9b74428d228281b623fa5d3c9f703c86ebc6b2c71a22742a338d310.png';

const strengthData = [
  { v: 45 }, { v: 62 }, { v: 55 }, { v: 78 }, { v: 72 }, { v: 85 }, { v: 82 },
];

const ReportCard = () => (
  <div
    className="glass-card"
    style={{
      width: '340px',
      padding: '28px',
      position: 'relative',
      animation: 'float 6s ease-in-out infinite',
      background: 'rgba(255,255,255,0.85)',
      border: '1px solid rgba(255,255,255,0.9)',
      boxShadow: '0 32px 80px rgba(42,157,143,0.14), 0 8px 24px rgba(0,0,0,0.06)',
    }}
    data-testid="hero-report-card"
  >
    {/* Header */}
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
      <div>
        <div className="section-label" style={{ fontSize: '10px', letterSpacing: '0.16em', marginBottom: '4px' }}>Assessment Report</div>
        <div style={{ fontSize: '15px', fontFamily: 'Manrope,sans-serif', fontWeight: 600, color: '#1A202C' }}>Aryan K.</div>
        <div style={{ fontSize: '12px', color: '#64748B', marginTop: '2px' }}>Class 11 · Science Stream</div>
      </div>
      <div style={{
        width: '52px', height: '52px', borderRadius: '50%',
        background: 'linear-gradient(135deg, #EDF7F5, #D0EEE9)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: '20px', fontFamily: 'Manrope', fontWeight: 700, color: '#2A9D8F'
      }}>82</div>
    </div>

    {/* Sparkline */}
    <div style={{ height: '48px', margin: '0 -4px 16px' }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={strengthData}>
          <defs>
            <linearGradient id="heroGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#2A9D8F" stopOpacity={0.2} />
              <stop offset="95%" stopColor="#2A9D8F" stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area type="monotone" dataKey="v" stroke="#2A9D8F" strokeWidth={2} fill="url(#heroGrad)" dot={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>

    {/* Strength Tags */}
    <div style={{ marginBottom: '16px' }}>
      <div style={{ fontSize: '11px', fontFamily: 'Manrope', fontWeight: 600, color: '#64748B', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '8px' }}>
        Key Strengths
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {['Critical Thinking', 'Emotional IQ', 'Learning Agility'].map((s) => (
          <span key={s} style={{
            fontSize: '11px', fontFamily: 'Inter', fontWeight: 500,
            color: '#2A9D8F', background: 'rgba(42,157,143,0.09)',
            padding: '4px 10px', borderRadius: '99px',
            border: '1px solid rgba(42,157,143,0.18)'
          }}>{s}</span>
        ))}
      </div>
    </div>

    {/* CTA */}
    <button style={{
      width: '100%', padding: '10px', borderRadius: '12px',
      background: 'linear-gradient(135deg, #2A9D8F, #228B7E)',
      color: 'white', fontFamily: 'Manrope', fontWeight: 600, fontSize: '13px',
      border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center',
      justifyContent: 'center', gap: '6px', letterSpacing: '0.01em'
    }} data-testid="hero-card-cta">
      View Full Report <ArrowRight size={14} />
    </button>

    {/* Floating satellite badges */}
    <div style={{
      position: 'absolute', top: '-14px', right: '-14px',
      background: '#F4A261', color: 'white', borderRadius: '12px',
      padding: '6px 12px', fontSize: '11px', fontFamily: 'Manrope', fontWeight: 700,
      boxShadow: '0 4px 16px rgba(244,162,97,0.35)',
      display: 'flex', alignItems: 'center', gap: '4px'
    }}>
      <Star size={10} fill="white" /> AI Analysed
    </div>
  </div>
);

const FloatingStatCard = ({ value, label, delay, style }) => (
  <div
    className="glass-card"
    style={{
      padding: '14px 18px',
      display: 'flex', alignItems: 'center', gap: '12px',
      animation: `float ${4 + delay}s ease-in-out ${delay}s infinite`,
      ...style
    }}
  >
    <div style={{ fontSize: '22px', fontFamily: 'Manrope', fontWeight: 700, color: '#2A9D8F' }}>{value}</div>
    <div style={{ fontSize: '12px', color: '#64748B', fontFamily: 'Inter' }}>{label}</div>
  </div>
);

const Hero = ({ onOpenAssessment, onOpenPlans }) => {
  const [ref, visible] = useScrollReveal({ threshold: 0.05 });

  return (
    <section
      id="hero"
      ref={ref}
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        padding: '136px 24px 64px',
        position: 'relative',
        overflow: 'hidden',
        background: '#F9F9F7',
      }}
    >
      {/* Ambient glow backgrounds */}
      <div style={{
        position: 'absolute', top: '-100px', left: '-120px',
        width: '600px', height: '600px',
        background: 'radial-gradient(ellipse at center, rgba(42,157,143,0.09) 0%, transparent 70%)',
        borderRadius: '50%', pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute', bottom: '-80px', right: '-100px',
        width: '500px', height: '500px',
        background: 'radial-gradient(ellipse at center, rgba(244,162,97,0.06) 0%, transparent 70%)',
        borderRadius: '50%', pointerEvents: 'none',
      }} />

      <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '48px', flexWrap: 'wrap' }}>

          {/* LEFT: Text */}
          <div
            style={{ flex: '1 1 320px', minWidth: 0, maxWidth: '560px' }}
            className={`reveal ${visible ? 'visible' : ''}`}
          >
            {/* Label badge */}
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              background: 'rgba(42,157,143,0.1)', border: '1px solid rgba(42,157,143,0.22)',
              borderRadius: '99px', padding: '6px 14px', marginBottom: '28px',
            }}>
              <Sparkles size={12} color="#2A9D8F" />
              <span style={{ fontSize: '12px', fontFamily: 'Manrope', fontWeight: 600, color: '#2A9D8F', letterSpacing: '0.06em' }}>
                AI-Powered Career & Psychological Guidance
              </span>
            </div>

            <h1 style={{
              fontFamily: 'Manrope, sans-serif',
              fontSize: 'clamp(30px, 5.5vw, 68px)',
              fontWeight: 300,
              lineHeight: 1.05,
              color: '#1A202C',
              margin: '0 0 8px',
              letterSpacing: '-0.02em',
            }}>
              Understand
            </h1>
            <h1 style={{
              fontFamily: 'Manrope, sans-serif',
              fontSize: 'clamp(30px, 5.5vw, 68px)',
              fontWeight: 700,
              lineHeight: 1.05,
              color: '#2A9D8F',
              margin: '0 0 24px',
              letterSpacing: '-0.02em',
            }}>
              Potential.<br />Guide Growth.
            </h1>

            <p style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: '17px',
              lineHeight: 1.72,
              color: '#64748B',
              margin: '0 0 36px',
              maxWidth: '460px',
            }}>
              Scientific assessments, AI-supported insights, and expert counseling —
              helping students and parents make confident choices about streams, subjects, colleges, and career roadmaps.
            </p>

            {/* CTAs */}
            <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '36px' }}>
              <button className="btn-primary" data-testid="hero-start-assessment-btn" onClick={() => onOpenAssessment?.()}>
                Start Assessment <ArrowRight size={16} />
              </button>
              <button className="btn-ghost" data-testid="hero-explore-plans-btn" onClick={() => onOpenPlans?.()}>
                Explore Plans
              </button>
            </div>

            {/* Trust microline */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
              {[
                { icon: <Shield size={13} />, text: 'Secure & Private' },
                { icon: <Brain size={13} />, text: 'Career + Psychology' },
                { icon: <Users size={13} />, text: 'Parent-Friendly' },
                { icon: <Star size={13} />, text: 'Expert-Supported' },
              ].map(({ icon, text }) => (
                <div key={text} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <span style={{ color: '#2A9D8F' }}>{icon}</span>
                  <span style={{ fontSize: '12px', color: '#64748B', fontFamily: 'Inter', fontWeight: 500 }}>{text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT: Premium Visual */}
          <div
            style={{ flex: '1 1 300px', minWidth: 0, position: 'relative', padding: '20px 0' }}
            className={`reveal reveal-delay-3 ${visible ? 'visible' : ''}`}
          >
            {/* Brain-Tree AI image as immersive backdrop */}
            <div style={{
              position: 'relative', borderRadius: '32px', overflow: 'hidden',
              boxShadow: '0 40px 120px rgba(42,157,143,0.18), 0 10px 40px rgba(0,0,0,0.08)',
              maxWidth: '480px', margin: '0 auto',
            }}>
              <img
                src={BRAIN_TREE_IMG}
                alt="AI-guided psychological insight visualization"
                style={{
                  width: '100%', height: '440px', objectFit: 'cover',
                  objectPosition: 'center',
                  display: 'block',
                  filter: 'saturate(0.88) brightness(1.05)',
                }}
              />
              {/* Soft bottom gradient for card integration */}
              <div style={{
                position: 'absolute', inset: 0,
                background: 'linear-gradient(to bottom, transparent 40%, rgba(249,249,247,0.22) 100%)',
              }} />

              {/* Floating report card over image */}
              <div style={{
                position: 'absolute', bottom: '24px', right: '20px', left: '20px',
                background: 'rgba(255,255,255,0.88)',
                backdropFilter: 'blur(20px)',
                borderRadius: '20px',
                padding: '18px 20px',
                boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
                border: '1px solid rgba(255,255,255,0.9)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <div>
                    <div style={{ fontSize: '10px', fontFamily: 'Manrope', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#2A9D8F', marginBottom: '3px' }}>Career Guidance Report</div>
                    <div style={{ fontFamily: 'Manrope', fontWeight: 700, fontSize: '14px', color: '#1A202C' }}>Aryan K. — Class 11</div>
                  </div>
                  <div style={{
                    width: '44px', height: '44px', borderRadius: '50%',
                    background: 'linear-gradient(135deg, #2A9D8F, #228B7E)',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    color: 'white',
                  }}>
                    <div style={{ fontFamily: 'Manrope', fontWeight: 800, fontSize: '15px', lineHeight: 1 }}>82</div>
                    <div style={{ fontSize: '8px', fontWeight: 600 }}>SCORE</div>
                  </div>
                </div>
                {/* Sparkline */}
                <div style={{ height: '36px', margin: '0 -4px 10px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={[{v:62},{v:68},{v:72},{v:78},{v:82}]}>
                      <defs>
                        <linearGradient id="hGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#2A9D8F" stopOpacity={0.2} />
                          <stop offset="95%" stopColor="#2A9D8F" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <Area type="monotone" dataKey="v" stroke="#2A9D8F" strokeWidth={2} fill="url(#hGrad)" dot={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div style={{ display: 'flex', gap: '5px' }}>
                  {['Aptitude Map', 'Stream Fit', 'Learning Style'].map((s) => (
                    <span key={s} style={{ fontSize: '9px', fontFamily: 'Inter', fontWeight: 500, color: '#2A9D8F', background: 'rgba(42,157,143,0.09)', padding: '3px 7px', borderRadius: '99px' }}>{s}</span>
                  ))}
                </div>
              </div>

              {/* AI badge */}
              <div style={{
                position: 'absolute', top: '18px', left: '18px',
                background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(10px)',
                borderRadius: '12px', padding: '7px 12px',
                display: 'flex', alignItems: 'center', gap: '6px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
              }}>
                <Star size={10} fill="#F4A261" color="#F4A261" />
                <span style={{ fontSize: '10px', fontFamily: 'Manrope', fontWeight: 700, color: '#1A202C' }}>AI Career Roadmap</span>
              </div>
            </div>

            {/* Floating stat cards */}
            <FloatingStatCard
              value="2,400+"
              label="Assessments Done"
              delay={1.5}
              style={{
                position: 'absolute', bottom: '-10px', left: '-15px', zIndex: 3,
                fontSize: '12px', minWidth: '160px',
              }}
            />
            <FloatingStatCard
              value="94%"
              label="Parent Satisfaction"
              delay={2.2}
              style={{
                position: 'absolute', top: '10px', right: '-10px', zIndex: 3,
                fontSize: '12px', minWidth: '150px',
              }}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
